import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './JobComment.less';
import Dropdown, { DropdownEntryType } from '../Dropdown/Dropdown';
import TextArea from '../TextArea/TextArea';
import { useTranslation } from 'react-i18next';
import { DefaultJobComment } from '../../../redux/application/application.types';

const FREE_TEXT_VALUE = 'Freitext';

const JobComment = ({
    textOnly = false,
    onChange,
    additionalComments,
    primary = false,
    isFreeTextOptionEnabled = true,
    dropdownPosition = 'absolute',
    initialValue,
}: {
    textOnly?: boolean;
    onChange: (text: string | undefined) => void;
    additionalComments?: DefaultJobComment[];
    primary?: boolean;
    isFreeTextOptionEnabled?: boolean;
    dropdownPosition?: 'relative' | 'absolute';
    // Pre-fills the comment, e.g. with a hint that already came from an Excel
    // import. Matches a predefined option (case-insensitively) if there is one,
    // otherwise falls back to the free-text ("anderer Hinweis") option. Only
    // applied once, on mount — callers that need to re-initialize for a
    // different entry must remount (e.g. via a `key` prop keyed on the entry's
    // identity).
    initialValue?: string;
}) => {
    const { t } = useTranslation();

    const options: DropdownEntryType[] = useMemo(
        () =>
            [
                !textOnly && {
                    value: 'Zähler wurde gewechselt',
                    id: 'Wechselname',
                    name: t('jobs.details.readingHint.meterChanged'),
                },
                ...((!textOnly && additionalComments) || []).map((additionalComment) => {
                    return {
                        value: additionalComment.text,
                        id: additionalComment.text,
                        name: t(`job.details.readingHint.custom.${additionalComment.text}`, additionalComment.text),
                    };
                }),
                isFreeTextOptionEnabled
                    ? {
                          value: FREE_TEXT_VALUE,
                          id: FREE_TEXT_VALUE,
                          name: t('jobs.details.readingHint.freetext.label'),
                      }
                    : null,
            ]
                .filter((value) => !!value)
                .map((element) => element as DropdownEntryType),
        // `t` changes with the language: without it the labels kept the language that was active
        // when the job was opened, only the placeholder switched (qa#134).
        [t, textOnly, additionalComments, isFreeTextOptionEnabled]
    );

    // Ein vorbelegter Hinweis, der einen vordefinierten Eintrag wortgleich
    // trifft (Groß-/Kleinschreibung und Leerraum egal), wählt diesen Eintrag
    // aus, statt als freier Text zu landen. Geprüft wird gegen den gespeicherten
    // Wert *und* die angezeigte Bezeichnung, damit auch ein übersetztes Label
    // aus der Excel-Datei trifft.
    const matchedOption = useMemo(() => {
        const normalized = (initialValue ?? '').trim().toLowerCase();
        if (!normalized) return undefined;

        return options.find(
            (option) =>
                option.value !== FREE_TEXT_VALUE &&
                (option.value.trim().toLowerCase() === normalized ||
                    (typeof option.name === 'string' && option.name.trim().toLowerCase() === normalized))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasInitialFreeText = !!initialValue && !matchedOption && isFreeTextOptionEnabled;
    const [comment, setComment] = useState<string | undefined>(
        matchedOption ? String(matchedOption.value) : hasInitialFreeText ? FREE_TEXT_VALUE : undefined
    );
    const [freeTextComment, setFreeTextComment] = useState(hasInitialFreeText ? initialValue! : '');

    const [showInput, setShowInput] = useState(hasInitialFreeText);
    const onChangeRef = useRef(onChange);
    const hasUserChangedCommentRef = useRef(false);
    const lastEmittedCommentRef = useRef<{ hasValue: boolean; value: string | undefined }>({
        hasValue: false,
        value: undefined,
    });

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Weicht der getroffene Eintrag in der Schreibweise vom vorbelegten Text ab
    // („leerstand" → „Leerstand"), muss der Aufrufer die kanonische Fassung
    // bekommen — sonst zeigt das Dropdown etwas anderes an, als übermittelt wird.
    useEffect(() => {
        if (!matchedOption) return;

        const canonical = String(matchedOption.value);
        if (canonical === initialValue) return;

        lastEmittedCommentRef.current = { hasValue: true, value: canonical };
        onChangeRef.current(canonical);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasUserChangedCommentRef.current) {
            return;
        }

        const nextComment = comment !== FREE_TEXT_VALUE ? comment : freeTextComment;
        const lastEmittedComment = lastEmittedCommentRef.current;

        if (lastEmittedComment.hasValue && lastEmittedComment.value === nextComment) {
            return;
        }

        lastEmittedCommentRef.current = {
            hasValue: true,
            value: nextComment,
        };
        onChangeRef.current(nextComment);
    }, [comment, freeTextComment]);

    const valueChange = useCallback((value: string) => {
        hasUserChangedCommentRef.current = true;
        setComment(value);
        if (value === FREE_TEXT_VALUE) {
            setShowInput(true);
        } else {
            setShowInput(false);
            setFreeTextComment('');
        }
    }, []);

    const setFreeTextValue = useCallback((value: string) => {
        hasUserChangedCommentRef.current = true;
        setFreeTextComment(value);
    }, []);

    return (
        <div className="job-comment">
            <Dropdown
                dropdownPosition={dropdownPosition}
                primary={primary}
                placeholder={t('jobs.details.readingHint.placeholder')}
                allowCancel={true}
                values={options}
                value={comment}
                onChange={valueChange}
            />

            {isFreeTextOptionEnabled && showInput && (
                <TextArea
                    hasFocus={showInput}
                    maxLength={1000}
                    placeholder={t('jobs.details.readingHint.freetext.placeholder')}
                    value={freeTextComment}
                    onChange={setFreeTextValue}
                />
            )}
        </div>
    );
};

export default JobComment;
